import {
    AccessorialType,
    ACCESSORIAL_TYPE_LABEL,
    ADJUSTMENT_ACCESSORIAL_TYPES,
    InvoiceAdjustmentDecision,
    InvoiceAdjustmentKind,
    invoiceAdjustmentTypeLabel,
} from '@numeo/types-core'
import { RateconRevisionInvoiceContext } from '@numeo/types-main-web-app'

/** What the dispatcher has typed into the charge fields. `null` means "untouched": the amount is then taken from the
 *  suggested difference and the type defaults to a line haul revision. Billing nothing is the "Accept only" button, not
 *  a value here. */
export interface BillingDraft {
    amount: string | null
    kind: InvoiceAdjustmentKind | null
    /** The exact charge, when the kind is an additional charge. */
    accessorialType: AccessorialType | null
    note: string
}

export const EMPTY_BILLING_DRAFT: BillingDraft = { amount: null, kind: null, accessorialType: null, note: '' }

const CHARGE_PREFIX = 'charge:'

/** What the Type select holds: the line haul revision, or one exact charge (`charge:DETENTION`). */
export const billingTypeValue = (kind: InvoiceAdjustmentKind | 'unset', accessorialType: AccessorialType | null): string => {
    if (kind === 'unset') return ''
    if (kind === InvoiceAdjustmentKind.ADDITIONAL_CHARGE) return `${CHARGE_PREFIX}${accessorialType ?? AccessorialType.OTHER}`
    return kind
}

/** The draft a Type choice makes: the kind, and the exact charge when it is one. */
export const applyBillingType = (draft: BillingDraft, value: string): BillingDraft => {
    if (value.startsWith(CHARGE_PREFIX)) {
        return { ...draft, kind: InvoiceAdjustmentKind.ADDITIONAL_CHARGE, accessorialType: value.slice(CHARGE_PREFIX.length) as AccessorialType }
    }
    return { ...draft, kind: value as InvoiceAdjustmentKind, accessorialType: null }
}

/** The Type select's choices: the revision of the line haul, then each exact charge a broker is billed for. */
export const BILLING_TYPE_SECTIONS = [
    { items: [{ label: 'Line haul revision', value: InvoiceAdjustmentKind.LINE_HAUL_REVISION }] },
    { title: 'Charge', items: ADJUSTMENT_ACCESSORIAL_TYPES.map((type) => ({ label: ACCESSORIAL_TYPE_LABEL[type], value: `${CHARGE_PREFIX}${type}` })) },
]

export type BillingResolution = { ok: true; adjustment: InvoiceAdjustmentDecision } | { ok: false; reason: string }

const cents = (value: number): number => Math.round(value * 100) / 100

export const formatUsd = (amount: number): string => `$${Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export const formatSignedUsd = (amount: number): string => `${amount < 0 ? '-' : '+'}${formatUsd(amount)}`

/** The difference the server would suggest for this rate, or null when it cannot: the rate did not change, or the load
 *  was re-rated after it was invoiced so the billed amount is not the revision's baseline. */
const autoAmount = (context: RateconRevisionInvoiceContext, rateChanged: boolean, effectiveRate: number): number | null =>
    rateChanged && context.confident ? cents(effectiveRate - context.billedAmount) : null

/** Whether the rate accepting writes differs from what was already billed — i.e. there is a difference worth suggesting
 *  as a charge by default. False means nothing to suggest; the dispatcher can still add a charge by hand. */
export const needsBilling = (context: RateconRevisionInvoiceContext, rateChanged: boolean, effectiveRate: number): boolean =>
    rateChanged && cents(effectiveRate) !== cents(context.billedAmount)

/** The amount the fields show: what was typed, else the suggested difference, else empty. */
export const billingAmountText = (draft: BillingDraft, context: RateconRevisionInvoiceContext, rateChanged: boolean, effectiveRate: number): string => {
    if (draft.amount !== null) return draft.amount
    const auto = autoAmount(context, rateChanged, effectiveRate)
    return auto ? String(auto) : ''
}

/** The charge type the fields show: what was chosen, else a line haul revision for a rate change, else nothing yet. A rate
 *  change is a line haul revision by default — the dispatcher only states the amount (unless they pick a charge instead);
 *  a charge on a revision that changes no rate has its type chosen by hand. */
export const billingKind = (draft: BillingDraft, rateChanged: boolean): InvoiceAdjustmentKind | 'unset' => {
    if (draft.kind) return draft.kind
    if (rateChanged) return InvoiceAdjustmentKind.LINE_HAUL_REVISION
    return 'unset'
}

/** The charge the fields resolve to, or why they are not ready. Always a real amount and type — billing nothing is the
 *  Accept-only button, never a resolution here. */
export const resolveBilling = (draft: BillingDraft, context: RateconRevisionInvoiceContext, rateChanged: boolean, effectiveRate: number): BillingResolution => {
    const kind = billingKind(draft, rateChanged)
    if (kind === 'unset') return { ok: false, reason: 'Choose what the charge is for.' }
    const text = billingAmountText(draft, context, rateChanged, effectiveRate).trim()
    if (!text) return { ok: false, reason: 'Enter the amount to charge.' }
    const amount = Number(text)
    if (!Number.isFinite(amount) || cents(amount) === 0) return { ok: false, reason: 'The amount must be a number other than zero.' }
    const note = draft.note.trim()
    const accessorialType = kind === InvoiceAdjustmentKind.ADDITIONAL_CHARGE ? (draft.accessorialType ?? AccessorialType.OTHER) : null
    return { ok: true, adjustment: { amount: cents(amount), kind, ...(accessorialType && { accessorialType }), ...(note && { note }) } }
}

/** The line under the fields: how the amount came about and what accepting does with it. */
export const describeBillingOutcome = (
    context: RateconRevisionInvoiceContext,
    resolution: BillingResolution,
    rateChanged: boolean,
    effectiveRate: number
): string => {
    if (!resolution.ok) return resolution.reason
    const { amount, kind } = resolution.adjustment
    const word = amount < 0 ? 'credit' : 'charge'
    // The arithmetic explains a line haul revision only: a detention or a lumper is not a difference of two rates.
    if (kind === InvoiceAdjustmentKind.LINE_HAUL_REVISION && rateChanged) {
        return `Revised ${formatUsd(effectiveRate)} − billed ${formatUsd(context.billedAmount)} = ${formatSignedUsd(cents(effectiveRate - context.billedAmount))}. ${formatSignedUsd(amount)} ${word} will wait in Ready to Invoice.`
    }
    const label = invoiceAdjustmentTypeLabel({ kind, accessorialType: resolution.adjustment.accessorialType })
    return `${formatSignedUsd(amount)} ${kind === InvoiceAdjustmentKind.ADDITIONAL_CHARGE ? `${label} ` : ''}${word} will wait in Ready to Invoice.`
}
